import { fetchJson, postJson } from "@/helpers/fetch";
import { getIsoDateFormat } from "@/helpers/utils";
import { Cadence, RechargeAddress, RechargeItemRaw, Charge } from "./types";

type UpdateSubscriptionResponse = {
  subscription: RechargeItemRaw;
};
type ProcessChargeResponse = {
  charges: Charge[];
};
type SkipSubscriptionResponse = {
  charge: Charge;
};
type UpdatedSubAndOneTimeResponse = {
  subscriptions: RechargeItemRaw[];
  onetimes: RechargeItemRaw[];
};
type UpdateBulkSubscriptionsResponse = {
  subscriptions: RechargeItemRaw[];
};
type UpdateOnetimeResponse = {
  onetime: RechargeItemRaw;
};
type UpdateAddressResponse = {
  address: Omit<RechargeAddress, "discount">;
};

type GetAddressResponse = {
  addresses: RechargeAddress;
};

type RechargeSubscriptionWithoutProduct = RechargeItemRaw;

export const generateRechargeUrl = (
  path: string,
  params?: Record<string, string>
) => {
  try {
    const url = new URL(path, window.location.origin);
    const current = [...new URL(window.location.toString()).searchParams];
    const preserve = [
      "token",
      "preview_standard_theme",
      "preview_theme",
      "preview_token",
    ];
    url.search = new URLSearchParams({
      ...Object.fromEntries(current.filter(([key]) => preserve.includes(key))),
      ...params,
    }).toString();
    return url.href;
  } catch (e) {
    return path;
  }
};

export const rechargeBase = window.location.pathname.match(
  /\/tools\/recurring\/portal\/[^\/]+/g
);

export const cancelSubscription = async (orderId: number) => {
  const cancelUrl = generateRechargeUrl(
    rechargeBase + `/subscriptions/${orderId}/cancel`
  );
  const response = await postJson<UpdateSubscriptionResponse>(cancelUrl, {
    body: {
      cancellation_reason: "na",
      cancellation_reason_comments: "",
    },
  });

  return response.subscription;
};

export const cancelOnetime = async (orderId: number): Promise<void> => {
  const cancelUrl = generateRechargeUrl(
    rechargeBase + `/onetimes/${orderId}/cancel`
  );
  await fetchJson(cancelUrl, { method: "POST" });
};

export const removePortalDiscountCode = async (
  addressId: number
): Promise<RechargeAddress> => {
  const url = generateRechargeUrl(
    rechargeBase + `/addresses/${addressId}/remove_discount`
  );
  const response = await fetchJson<UpdateAddressResponse>(url, {
    method: "post",
  });

  return response.address;
};

export const applyPortalDiscountCode = async (
  addressId: number,
  discountCode: string
): Promise<RechargeAddress> => {
  const discountUrl = generateRechargeUrl(
    rechargeBase + `/addresses/${addressId}/apply_discount`
  );
  const discountResponse = await postJson<UpdateAddressResponse>(discountUrl, {
    body: { discount_code: discountCode },
  });

  // recharge does not return discount code on this fetch, so we must query for it.
  const addressWithoutDiscount = discountResponse.address;
  const schema = `{ "addresses": { "id": ${addressWithoutDiscount.id}, "discount": {"id": "parent.discount_id"} }}`;
  const url = generateRechargeUrl(rechargeBase + `/request_objects`, {
    schema,
  });
  const addressWithDiscount = await fetchJson<GetAddressResponse>(url);
  return addressWithDiscount.addresses;
};

export const updateNextChargeDate = async (request: {
  orderId: number;
  nextChargeDate: Date;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  const { orderId, nextChargeDate: date } = request;

  const nextChargeDateUrl = generateRechargeUrl(
    rechargeBase + `/subscriptions/${orderId}/set_next_charge_date`
  );
  const response = await postJson<UpdateSubscriptionResponse>(
    nextChargeDateUrl,
    {
      body: {
        date: getIsoDateFormat(date),
      },
    }
  );

  return response.subscription;
};

export const fetchCharges = async (request: {
  subscriptionId: number;
}): Promise<Charge[]> => {
  const schema = `{ "charges": {"subscription_id":${request.subscriptionId},"status":"QUEUED"}}`;
  const url = generateRechargeUrl(rechargeBase + "/request_objects", {
    schema,
  });
  const response = await fetchJson<ProcessChargeResponse>(url);
  return response.charges;
};

export const fetchChargesByID = async (request: {
  subscriptionId: number;
  chargeID: number;
}): Promise<Charge[]> => {
  const schema = `{ "charges": {"subscription_id":${request.subscriptionId},"ids":${request.chargeID}}}`;
  const url = generateRechargeUrl(rechargeBase + "/request_objects", {
    schema,
  });
  const response = await fetchJson<ProcessChargeResponse>(url);
  return response.charges;
};

async function postWithoutHeader(url: string): Promise<any> {
  try {
    const response = await fetch(url, {
      method: "post",
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const processCharge = async (request: {
  chargeId: number;
}): Promise<Charge> => {
  const { chargeId } = request;
  const chargeUrl = generateRechargeUrl(
    rechargeBase + `/charges/${chargeId}/process`
  );
  const response = await postWithoutHeader(chargeUrl);
  return response.charge;
};

export const skipCharge = async (request: {
  chargeId: number;
  subscriptionIds: number[];
}): Promise<Charge> => {
  const { chargeId, subscriptionIds: subscription_ids } = request;
  const url = generateRechargeUrl(rechargeBase + `/charges/${chargeId}/skip`);
  const response = await postJson<{ charge: Charge }>(url, {
    body: {
      subscription_ids,
    },
  });
  return response.charge;
};

export const skipSubscription = async (request: {
  subscriptionId: number;
}): Promise<Charge> => {
  const { subscriptionId } = request;
  const url = generateRechargeUrl(
    rechargeBase + `/subscriptions/${subscriptionId}/skip`
  );
  const response = await postJson<SkipSubscriptionResponse>(url, {
    body: {},
  });
  return response.charge;
};

export const fetchSubscriptions = async (): Promise<RechargeItemRaw[]> => {
  const schema =
    '{ "subscriptions": { "product": {} }, "onetimes": { "product": {} }}';
  const url = generateRechargeUrl(rechargeBase + `/request_objects`, {
    schema,
  });
  const allItemsResponse = await fetchJson<UpdatedSubAndOneTimeResponse>(url);
  const subscriptionAndOnetimes = [
    ...allItemsResponse.subscriptions,
    ...allItemsResponse.onetimes,
  ];
  return subscriptionAndOnetimes;
};

export const updateOneTimeProduct = async (request: {
  orderId: number;
  shopifyVariantId?: number;
  quantity?: number;
  nextChargeDate?: Date;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  const { orderId, shopifyVariantId, quantity, nextChargeDate } = request;
  const updateOneTimeUrl = generateRechargeUrl(
    rechargeBase + `/onetimes/${orderId}`
  );
  const response = await postJson<UpdateOnetimeResponse>(updateOneTimeUrl, {
    body: {
      quantity,
      shopify_variant_id: shopifyVariantId,
      next_charge_scheduled_at: getIsoDateFormat(nextChargeDate ?? new Date()),
    },
  });

  return response.onetime;
};

export async function updateBulkSubscriptions(
  addressId: number | string,
  data: {
    subscriptionId: number;
    nextChargeDate: Date;
  }[]
) {
  const subscriptions = data.map((item) => {
    const { subscriptionId: id, nextChargeDate: next_charge_scheduled_at } =
      item;
    return {
      id,
      next_charge_scheduled_at: getIsoDateFormat(next_charge_scheduled_at),
    };
  });
  const url = generateRechargeUrl(
    rechargeBase + `/addresses/${addressId}/subscriptions-bulk-update`
  );
  const response = await postJson<UpdateBulkSubscriptionsResponse>(url, {
    body: {
      subscriptions,
    },
  });
  return response.subscriptions;
}

export async function updateBulkOneTimeProduct(
  items: {
    orderId: number;
    shopifyVariantId?: number;
    quantity?: number;
    nextChargeDate?: Date;
  }[]
) {
  const results = [];

  for (const item of items) {
    try {
      const request = {
        orderId: item.orderId,
        shopifyVariantId: item.shopifyVariantId,
        quantity: item.quantity,
        nextChargeDate: item.nextChargeDate,
      };

      const result = await updateOneTimeProduct(request);
      results.push(result);
    } catch (error) {
      console.error("Error during request:", error);
      // Handle the error as needed, e.g., break the loop or continue to the next iteration
    }
  }
  return results;
}

export const updateSubscription = async (request: {
  orderId: number;
  shopifyVariantId?: number;
  quantity?: number;
  cadence?: Cadence;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  const { orderId, shopifyVariantId, quantity, cadence } = request;

  const updateSubscriptionUrl = generateRechargeUrl(
    rechargeBase + "/subscriptions/" + orderId
  );
  const response = await postJson<UpdateSubscriptionResponse>(
    updateSubscriptionUrl,
    {
      body: {
        ...(quantity && { quantity }),
        ...(shopifyVariantId && {
          shopify_variant_id: shopifyVariantId,
        }),
        ...(cadence && {
          charge_interval_frequency: cadence.frequency.toString(),
          order_interval_frequency: cadence.frequency.toString(),
          order_interval_unit: cadence.unit.unit,
        }),
      },
    }
  );
  return response.subscription;
};

export const updateAddress = async (request: {
  orderId: number;
  addressId: number;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  const { orderId, addressId } = request;
  const updateSubscriptionUrl = generateRechargeUrl(
    rechargeBase + "/subscriptions/" + orderId + "/move/address/" + addressId
  );
  const response = await postJson<UpdateSubscriptionResponse>(
    updateSubscriptionUrl,
    {
      body: {
        address_id: addressId,
      },
    }
  );
  return response.subscription;
};

export const createOnetimeItem = async (request: {
  shopifyVariantId: number;
  quantity: number;
  addressId: number;
  nextChargeDate: Date;
  properties?: Record<string, string | number>;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  const { shopifyVariantId, quantity, addressId, properties } = request;
  const createOnetimeUrl = generateRechargeUrl(rechargeBase + "/onetimes");
  const response = await postJson<UpdateOnetimeResponse>(createOnetimeUrl, {
    body: {
      shopify_variant_id: shopifyVariantId,
      quantity,
      address_id: addressId,
      next_charge_scheduled_at: getIsoDateFormat(request.nextChargeDate),
      ...properties,
    },
  });
  return response.onetime;
};

export const createSubscriptionItem = async (
  request: {
    shopifyVariantId: number;
    quantity: number;
    addressId: number;
    cadence: Cadence;
    nextChargeDate: Date;
    properties?: Record<string, string | number>;
  },
  requestUrl?: string
): Promise<RechargeSubscriptionWithoutProduct> => {
  const {
    shopifyVariantId,
    quantity,
    addressId,
    cadence,
    nextChargeDate,
    properties,
  } = request;
  const createSubscriptionUrl =
    requestUrl || generateRechargeUrl(rechargeBase + "/subscriptions");
  const response = await postJson<UpdateSubscriptionResponse>(
    createSubscriptionUrl,
    {
      body: {
        shopify_variant_id: shopifyVariantId,
        quantity,
        address_id: addressId,
        charge_interval_frequency: cadence.frequency.toString(),
        order_interval_frequency: cadence.frequency.toString(),
        order_interval_unit: cadence.unit.unit,
        next_charge_scheduled_at: getIsoDateFormat(nextChargeDate),
        ...properties,
      },
    }
  );
  return response.subscription;
};

export const reactivateSubscription = async (request: {
  subscriptionId: number;
}): Promise<RechargeSubscriptionWithoutProduct> => {
  let url = generateRechargeUrl(
    `${rechargeBase}/subscriptions/${request.subscriptionId}/activate`
  );
  const response = await fetchJson<UpdateSubscriptionResponse>(url, {
    method: "POST",
  });
  return response.subscription;
};

export const getRechargeShippingAddressLink = () => {
  return generateRechargeUrl(rechargeBase + "/shipping");
};
